import { action, observable } from 'mobx';
import { autobind } from 'core-decorators';

class LoginStore {
  @observable isLogin = false;
  @observable userName = '';
  @observable isReady = false;

  @action.bound
  setLoginState(isLogin, userName = '') {
    this.isLogin = isLogin;
    this.userName = isLogin ? userName : '';
    this.isReady = true;
  }

  @autobind
  syncFromHeaderDom() {
    const nameElement = document.querySelector('.t_xpass_signin_u_name_i');
    const userName = nameElement && nameElement.textContent && nameElement.textContent.trim();
    if (userName) {
      this.setLoginState(true, userName);
    }
  }

  @autobind
  initialize(requestLoginInfo, requestAccount) {
    let disposed = false;
    let accountVersion = 0;
    let hasFullAccount = false;
    let refreshTimer;
    let pending = false;
    const refresh = () => {
      if (disposed || pending || !requestLoginInfo) {
        return;
      }
      pending = true;
      Promise.resolve()
        .then(requestLoginInfo)
        .then(response => {
          if (!disposed) {
            const loggedIn = Number(response.code) > 0;
            const account = response.data && response.data.account;
            this.setLoginState(loggedIn, account || '');
          }
        })
        .catch(() => {
          // A transport failure is not proof of logout; retain the last confirmed state.
        })
        .then(() => {
          pending = false;
        });
    };
    if (requestLoginInfo) {
      refresh();
      refreshTimer = setInterval(refresh, 3000);
      window.addEventListener('focus', refresh);
      return () => {
        disposed = true;
        clearInterval(refreshTimer);
        window.removeEventListener('focus', refresh);
      };
    }
    let retryTimer;
    let headerDomTimer;
    let navibar;
    let previousLogin;
    let previousLogout;
    let onLogin;
    let onLogout;
    let attempts = 0;
    const connect = () => {
      if (disposed) {
        return;
      }
      navibar = window.XOYO_top_navi;
      if (!navibar || !navibar.isLogin || !navibar.rending) {
        if (++attempts < 100) {
          retryTimer = setTimeout(connect, 100);
        }
        return;
      }
      // The header owns get_info and its environment. Consume its result rather than
      // XPASS.u_account or a second request with independently selected credentials.
      const callbacks = navibar._callbacks || {};
      previousLogin = callbacks.sin_callback_oklogin;
      previousLogout = callbacks.sin_callback_nologin;
      onLogin = name => {
        if (!disposed) {
          if (!hasFullAccount) {
            this.setLoginState(true, name);
          }
          const version = ++accountVersion;
          if (requestAccount) {
            Promise.resolve()
              .then(requestAccount)
              .then(account => {
                if (!disposed && version === accountVersion && account) {
                  hasFullAccount = true;
                  this.setLoginState(true, account);
                }
              })
              .catch(() => {
                // Account enrichment must not change the header-confirmed login state.
              });
          }
        }
        if (typeof previousLogin === 'function') {
          previousLogin(name);
        }
      };
      onLogout = () => {
        accountVersion++;
        hasFullAccount = false;
        if (!disposed) {
          this.setLoginState(false);
        }
        if (typeof previousLogout === 'function') {
          previousLogout();
        }
      };
      navibar.isLogin(onLogin, onLogout);
      // Registration is not a request: refresh also covers a header loaded before React.
      navibar.rending();
      this.syncFromHeaderDom();
      headerDomTimer = setTimeout(() => {
        if (!hasFullAccount) {
          this.syncFromHeaderDom();
        }
      }, 300);
    };
    connect();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearTimeout(headerDomTimer);
      const callbacks = navibar && navibar._callbacks;
      if (callbacks && callbacks.sin_callback_oklogin === onLogin) {
        callbacks.sin_callback_oklogin = previousLogin;
      }
      if (callbacks && callbacks.sin_callback_nologin === onLogout) {
        callbacks.sin_callback_nologin = previousLogout;
      }
    };
  }

  @autobind
  signIn() {
    return window.XPASS.signin();
  }

  @autobind
  logout() {
    const xpass = window.XPASS;
    if (xpass && typeof xpass.exit === 'function') {
      return xpass.exit();
    }
    const navibar = window.XOYO_top_navi;
    if (navibar && typeof navibar.exit === 'function') {
      return navibar.exit();
    }
  }
}

export default LoginStore;
